import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAccountBookStore } from '@/store/accountBookStore';
import SectionHeader from '../SectionHeader';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0,0,0,0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0,0,0,0.3)',
    }
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: 'var(--bg-elev-2)',
  '& .MuiTableCell-head': {
    color: 'var(--fg-strong)',
    fontWeight: 700,
    fontSize: '0.95rem',
    textAlign: 'center',
    border: `1px solid ${theme.palette.divider}`,
    padding: '14px 8px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: 'var(--bg-elev-1)',
  },
  '&:hover': {
    backgroundColor: 'var(--bg-muted)',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableCell-body': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '12px 8px',
    fontSize: '0.9rem',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'left',
  fontWeight: 500,
}));

const NumberCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'right',
  fontWeight: 600,
  fontFamily: 'var(--font-numeric)',
}));

const IncomeStatementSection: React.FC = () => {
  const theme = useTheme();
  const { dashboardData } = useAccountBookStore();

  const formatNumber = (num: number): string => {
    return num.toLocaleString('ko-KR');
  };

  const calculateProfitMargin = (revenue: number, expense: number): number => {
    if (revenue === 0) return 0;
    return Math.round(((revenue - expense) / revenue) * 100 * 100) / 100;
  };

  const totalRevenue = dashboardData.kpiData.totalIncome;
  const totalExpense = dashboardData.kpiData.totalExpense;
  const operatingProfit = dashboardData.kpiData.netIncome;
  const profitMargin = calculateProfitMargin(totalRevenue, totalExpense);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        background: 'var(--bg-base)',
        borderRadius: 'var(--r-16)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <SectionHeader
        eyebrow="재무 상세"
        title="손익계산서"
        subtitle={`${dashboardData.selectedYear}년 매출 · 지출 · 영업이익`}
      />

      <StyledTableContainer>
        <Table size="small">
          <StyledTableHead>
            <TableRow>
              <StyledTableCell sx={{ width: '40%' }}>항목</StyledTableCell>
              <NumberCell sx={{ width: '30%' }}>금액</NumberCell>
              <NumberCell sx={{ width: '30%' }}>비율 (%)</NumberCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {/* 매출 섹션 */}
            <StyledTableRow>
              <StyledTableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: 'var(--primary-bg)',
                }}
              >
                매출
              </StyledTableCell>
              <NumberCell sx={{ fontWeight: 700, color: 'success.main', backgroundColor: 'var(--primary-bg)' }}>
                {formatNumber(totalRevenue)}
              </NumberCell>
              <NumberCell sx={{ fontWeight: 700, backgroundColor: 'var(--primary-bg)' }}>100.00%</NumberCell>
            </StyledTableRow>
            {/* 매출 카테고리별 상세 */}
            {dashboardData.revenueSources.length > 0 ? (
              dashboardData.revenueSources.map((item, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell sx={{ pl: 4 }}>
                    {item.category || '기타'}
                  </StyledTableCell>
                  <NumberCell sx={{ color: 'success.main' }}>{formatNumber(item.amount)}</NumberCell>
                  <NumberCell>{item.percentage.toFixed(2)}%</NumberCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                  매출 데이터가 없습니다
                </TableCell>
              </StyledTableRow>
            )}

            {/* 구분선 */}
            <TableRow>
              <TableCell colSpan={3} sx={{ 
                height: '2px', 
                padding: 0, 
                backgroundColor: theme.palette.divider,
                border: 'none'
              }} />
            </TableRow>

            {/* 지출 섹션 */}
            <StyledTableRow>
              <StyledTableCell 
                sx={{ 
                  fontWeight: 700, 
                  backgroundColor: 'var(--c-red-50)',
                }}
              >
                지출
              </StyledTableCell>
              <NumberCell sx={{ fontWeight: 700, color: 'error.main', backgroundColor: 'var(--c-red-50)' }}>
                {formatNumber(totalExpense)}
              </NumberCell>
              <NumberCell sx={{ fontWeight: 700, backgroundColor: 'var(--c-red-50)' }}>
                {totalRevenue > 0 ? ((totalExpense / totalRevenue) * 100).toFixed(2) : '0.00'}%
              </NumberCell>
            </StyledTableRow>
            {/* 지출 카테고리별 상세 */}
            {dashboardData.categoryExpenses.length > 0 ? (
              dashboardData.categoryExpenses.map((item, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell sx={{ pl: 4 }}>
                    {item.category || '기타'}
                  </StyledTableCell>
                  <NumberCell sx={{ color: 'error.main' }}>{formatNumber(item.amount)}</NumberCell>
                  <NumberCell>{item.percentage.toFixed(2)}%</NumberCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                  지출 데이터가 없습니다
                </TableCell>
              </StyledTableRow>
            )}

            {/* 영업이익 */}
            <StyledTableRow>
              <StyledTableCell 
                sx={{ 
                  fontWeight: 700,
                }}
              >
                영업이익
              </StyledTableCell>
              <NumberCell 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary'
                }}
              >
                {formatNumber(operatingProfit)}
              </NumberCell>
              <NumberCell 
                sx={{ 
                  fontWeight: 700,
                  color: 'text.primary'
                }}
              >
                {profitMargin >= 0 ? '+' : ''}{profitMargin.toFixed(2)}%
              </NumberCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Paper>
  );
};

export default IncomeStatementSection;

